import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Mic, Pause, Play, ChevronLeft, Gauge, Trophy, Volume2, AlertTriangle, Loader, Zap } from 'lucide-react';

const MAX_DISTANCE = 1000; // Total distance to "win"
const SENSITIVITY_THRESHOLD = 30; // Volume threshold (0-100) for *optimal* speed
const DRAG_FACTOR = 0.5; // Amount of distance lost per loop when volume is too low

export default function VibeRacerGame() {
    // Game State Management
    const [isRacing, setIsRacing] = useState(false);
    const [micVolume, setMicVolume] = useState(0); 
    const [distance, setDistance] = useState(0);    
    const [isFinished, setIsFinished] = useState(false);
    const [micStatus, setMicStatus] = useState('idle'); // 'idle', 'connecting', 'listening', 'error'
    
    // Web Audio API Refs
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const streamRef = useRef(null);
    const volumeDataRef = useRef(null);
    const frameRef = useRef(0);

    // --- AUDIO API SETUP AND CLEANUP ---
    const cleanupMicrophone = useCallback(() => {
        if (frameRef.current) cancelAnimationFrame(frameRef.current);
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            // Check if context is valid before closing
            if (audioContextRef.current.state !== 'closed' && audioContextRef.current.close) {
                audioContextRef.current.close().catch(e => console.error("Error closing AudioContext:", e));
            }
            audioContextRef.current = null;
        }
        setMicVolume(0);
        setMicStatus('idle');
    }, []);

    const setupMicrophone = useCallback(async () => {
        if (micStatus === 'listening' || micStatus === 'connecting') {
            if (audioContextRef.current && audioContextRef.current.state !== 'running') {
                await audioContextRef.current.resume().catch(e => console.warn("Failed to resume AudioContext:", e));
            }
            return;
        }

        setMicStatus('connecting');

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioContext;
            
            // SECURITY FIX: Must resume/start AudioContext inside a user-initiated handler
            await audioContext.resume(); 

            const source = audioContext.createMediaStreamSource(stream);
            
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256; 
            analyserRef.current = analyser;

            volumeDataRef.current = new Uint8Array(analyser.frequencyBinCount);
            
            source.connect(analyser);

            setMicStatus('listening');
        } catch (error) {
            console.error("Error accessing microphone:", error);
            setMicStatus('error');
        }
    }, [micStatus]);


    // --- 2. Real-time Volume Processing Loop (Updates micVolume state) ---
    useEffect(() => {
        if (micStatus !== 'listening' || isFinished) {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            return;
        }

        const processVolume = () => {
            const analyser = analyserRef.current;
            const dataArray = volumeDataRef.current;
            
            if (analyser && dataArray) {
                analyser.getByteTimeDomainData(dataArray);

                let sumOfSquares = 0;
                for (let i = 0; i < dataArray.length; i++) {
                    const value = dataArray[i] - 128; 
                    sumOfSquares += value * value;
                }
                const rms = Math.sqrt(sumOfSquares / dataArray.length);

                // Map RMS (0-128) to 0-100 state, adjusted for sensitivity
                const mappedVolume = Math.min(100, Math.floor((rms / 60) * 100));

                setMicVolume(mappedVolume);
            }
            frameRef.current = requestAnimationFrame(processVolume);
        };

        frameRef.current = requestAnimationFrame(processVolume);
        return () => cancelAnimationFrame(frameRef.current);
    }, [micStatus, isFinished]);


    // --- 3. Core Game Loop Logic (Distance Update) - FIX IMPLEMENTED HERE ---
    useEffect(() => {
        if (!isRacing || isFinished) return;

        let interval;
        const gameLoop = () => {
            // FIX: Use micVolume directly to determine speed, divided by a large factor 
            // to make sure it's a manageable distance increase (e.g., 100/100 = 1 unit per loop)
            const speedIncrease = micVolume / 100; // Max speed increase of 1 unit per loop

            let newDistance = distance + speedIncrease;
            
            // Apply drag if volume is low
            if (micVolume < 5 && distance > 0) {
                newDistance = Math.max(0, newDistance - DRAG_FACTOR);
            }

            if (newDistance >= MAX_DISTANCE) {
                setDistance(MAX_DISTANCE);
                setIsRacing(false);
                setIsFinished(true);
                cleanupMicrophone();
            } else {
                setDistance(newDistance);
            }
        };

        // Run the game loop every 50ms for smoother updates
        interval = setInterval(gameLoop, 50);

        return () => clearInterval(interval);
    }, [isRacing, micVolume, distance, isFinished, cleanupMicrophone]);


    // --- Helper Functions ---
    const startRace = async () => {
        if (micStatus === 'error') {
            alert("Cannot start race: Microphone access denied or failed.");
            return;
        }
        
        await setupMicrophone();

        setDistance(0);
        setIsFinished(false);
        setIsRacing(true);
    };

    const stopRace = () => {
        setIsRacing(false);
    };

    const progressPercent = (distance / MAX_DISTANCE) * 100;
    const volumePercent = (micVolume / 100) * 100;

    // Status message for the Mic meter
    const getMicMessage = () => {
        switch(micStatus) {
            case 'connecting':
                return "Connecting microphone...";
            case 'listening':
                return isRacing ? `Vibe: ${micVolume.toFixed(0)} - Speed Factor: ${(micVolume / 100).toFixed(2)}` : 'Mic Ready! Press Play.';
            case 'error':
                return "Mic Error: Check browser permissions!";
            default:
                return 'Ready to Play';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 to-teal-100 p-6 font-sans text-gray-800">
            <motion.header 
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="max-w-4xl mx-auto mb-10 text-center"
            >
                <h1 className="text-4xl font-extrabold tracking-wider text-pink-600 drop-shadow-md flex items-center justify-center gap-3">
                    <Zap className="w-8 h-8 text-yellow-500"/> VIBE RACER
                </h1>
                <p className="text-gray-600 mt-2 text-lg">
                    Speak loudly or make noise to generate energy and push your rocket forward!
                </p>
            </motion.header>

            {/* --- Game Container --- */}
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-6 border-4 border-pink-300">
                
                {/* Status Bar */}
                <div className="flex justify-between items-center mb-6 p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-center text-lg font-bold text-gray-700">
                        <Gauge className="w-5 h-5 mr-2 text-teal-600"/>
                        Distance: <span className="text-pink-600 ml-1">{distance.toFixed(0)}/{MAX_DISTANCE}</span>
                    </div>
                    
                    <motion.button 
                        onClick={isRacing ? stopRace : startRace}
                        className={`px-6 py-3 rounded-full text-white font-bold transition-all shadow-lg flex items-center gap-2 
                                    ${isFinished ? 'bg-teal-500 hover:bg-teal-600' : isRacing ? 'bg-red-500 hover:bg-red-600' : 'bg-pink-500 hover:bg-pink-600'}
                                    ${(micStatus === 'connecting' || micStatus === 'error') && !isRacing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={micStatus === 'connecting' || micStatus === 'error'}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {micStatus === 'connecting' && !isRacing ? <Loader className="w-5 h-5 animate-spin"/> : isRacing ? <Pause className="w-5 h-5"/> : <Play className="w-5 h-5"/>}
                        {micStatus === 'connecting' && !isRacing ? 'Connecting...' : isRacing ? 'PAUSE' : isFinished ? 'RACE AGAIN' : 'START RACE'}
                    </motion.button>
                </div>

                {/* --- Race Track Visual --- */}
                <div className="relative h-20 w-full bg-gray-200 border-4 border-gray-300 rounded-lg overflow-hidden">
                    {/* Checkered Finish Line */}
                    <div className="absolute right-0 top-0 bottom-0 w-8 z-10 
                                    bg-[repeating-linear-gradient(45deg,#cccccc,#cccccc_10px,#ffffff_10px,#ffffff_20px)]">
                        <Zap className="w-6 h-6 text-pink-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                    
                    {/* Progress Fill (Energy Trail) */}
                    <motion.div
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-teal-400 to-green-500 shadow-md shadow-teal-300"
                        animate={{ width: `${progressPercent}%` }}
                        transition={{ type: "spring", stiffness: 100, damping: 20 }}
                    />
                    
                    {/* Racer Icon */}
                    <motion.div
                        className="absolute top-1/2 -translate-y-1/2 w-16 h-16 flex items-center justify-center rounded-full bg-pink-500 shadow-xl border-4 border-white z-20"
                        initial={{ left: 0 }}
                        animate={{ 
                            left: `calc(${progressPercent}% - 32px)`, 
                            scale: isRacing ? 1.2 : 1, 
                            rotate: isRacing ? [0, 5, -5, 0] : 0 
                        }}
                        transition={{ 
                            left: { type: "spring", stiffness: 150, damping: 10 },
                            scale: { type: "spring", stiffness: 150, damping: 10 },
                            rotate: isRacing 
                                ? { duration: 0.3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" } 
                                : { duration: 0.1 } 
                        }}
                    >
                        <Rocket className="w-8 h-8 text-white" style={{ rotate: 90 }}/>
                    </motion.div>
                </div>
                
                {/* --- Volume Meter (Tachometer Design) --- */}
                <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-700 mb-3 flex items-center gap-2">
                        {micStatus === 'error' ? <AlertTriangle className="w-5 h-5 text-red-500"/> : <Mic className="w-5 h-5 text-pink-500"/>} 
                        Vibe Energy Tachometer
                    </h3>
                    <div className="relative h-8 w-full bg-gray-100 rounded-full overflow-hidden border-2 border-pink-500/50 shadow-inner">
                        <motion.div
                            className={`absolute top-0 left-0 h-full 
                                ${micVolume >= SENSITIVITY_THRESHOLD ? 'bg-gradient-to-r from-yellow-400 to-red-500' : 'bg-gradient-to-r from-yellow-300 to-orange-400'}`}
                            animate={{ width: `${volumePercent}%` }}
                            transition={{ duration: 0.1 }}
                        />
                        {/* Threshold Indicator Line (Red Zone Start) */}
                        <div 
                            className="absolute top-0 h-full w-1 bg-red-600 shadow-lg shadow-red-400 z-20"
                            style={{ left: `${SENSITIVITY_THRESHOLD}%` }}
                        />

                        <div className="absolute inset-0 flex items-center px-4 text-sm font-extrabold z-30">
                            <span className={micStatus === 'error' ? 'text-red-800' : 'text-gray-800'}>
                                {getMicMessage()}
                            </span>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Drag Warning! If volume stays below 5, you'll start to lose speed. Keep that energy up!
                    </p>
                </div>


                {/* --- Win/Lose Feedback --- */}
                <AnimatePresence>
                    {isFinished && (
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            className="mt-10 p-6 bg-green-100 border-4 border-green-400 rounded-xl text-center shadow-lg"
                        >
                            <Trophy className="w-10 h-10 text-yellow-600 mx-auto mb-3"/>
                            <h2 className="text-3xl font-extrabold text-green-700">CHALLENGE COMPLETE!</h2>
                            <p className="text-lg text-gray-700">Your high-energy vibe blasted the racer to the finish line!</p>
                            <button 
                                onClick={startRace}
                                className="mt-4 px-8 py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-full font-bold shadow-lg transition-colors"
                            >
                                RACE AGAIN
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            
            {/* Back Button */}
            <div className="mt-10 max-w-4xl mx-auto">
                <button
                    onClick={() => window.history.back()}
                    className="flex items-center gap-2 text-lg text-gray-600 hover:text-gray-800 transition-colors"
                >
                    <ChevronLeft className="w-6 h-6"/> Back to Game Select
                </button>
            </div>
        </div>
    );
}